import React, { useRef, useState } from "react";
import { Camera, Trash2, Gauge, Fuel, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export default function HandoverPhotos({ booking, user, onChanged }) {
  const photos = booking.handover_photos || [];
  const canAdd = [booking.customer_id, booking.owner_id, booking.driver_id].includes(user?.id) && ["confirmed", "completed"].includes(booking.status);
  return (
    <section className="rounded-2xl border border-[#E2E8F0] p-5 bg-white" data-testid="handover-photos">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">Handover photos</p>
          <p className="text-sm text-[#334155] mt-1">Both host and guest photograph the car at pickup and return. Photos are timestamped and become permanent evidence after 30 minutes — they're what we use to settle any damage dispute fairly.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        {["pickup", "return"].map((phase) => (
          <PhaseColumn key={phase} phase={phase} photos={photos.filter((p) => p.phase === phase)} booking={booking} user={user} canAdd={canAdd} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}

function PhaseColumn({ phase, photos, booking, user, canAdd, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ note: "", odometer_km: "", fuel_level: "" });

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/bookings/${booking.id}/photos`, {
        file_id: up.id, phase,
        note: meta.note,
        odometer_km: meta.odometer_km ? Number(meta.odometer_km) : null,
        fuel_level: meta.fuel_level || null,
      });
      toast.success(`${phase === "pickup" ? "Pickup" : "Return"} photo saved`);
      setMeta({ note: "", odometer_km: "", fuel_level: "" });
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (photo) => {
    try {
      await api.delete(`/bookings/${booking.id}/photos/${photo.id}`);
      toast.success("Photo removed");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove photo");
    }
  };

  return (
    <div className="rounded-xl bg-[#F7F8FA] border border-[#E2E8F0] p-4" data-testid={`handover-${phase}`}>
      <div className="flex items-center justify-between">
        <p className="font-display font-bold text-[#0a1f4d] capitalize">{phase}</p>
        <span className="text-xs text-[#64748B]">{photos.length} photo{photos.length === 1 ? "" : "s"}</span>
      </div>

      {photos.length === 0 && <p className="text-xs text-[#64748B] mt-2">No {phase} photos yet.</p>}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {photos.map((p) => {
          const mine = p.by_user_id === user?.id;
          const fresh = Date.now() - new Date(p.created_at).getTime() < 30 * 60 * 1000;
          return (
            <figure key={p.id} className="relative group" data-testid={`handover-photo-${p.id}`}>
              <a href={`${API_ORIGIN}${p.url}`} target="_blank" rel="noreferrer">
                <img src={`${API_ORIGIN}${p.url}`} alt={`${phase} by ${p.by_name}`} className="aspect-square w-full object-cover rounded-lg border border-[#E2E8F0]" />
              </a>
              <figcaption className="mt-1 text-[10px] leading-tight text-[#64748B]">
                <span className="font-semibold text-[#0a1f4d] capitalize">{p.by_role}</span> · {format(new Date(p.created_at), "d MMM HH:mm")}
                {(p.odometer_km || p.fuel_level) && <div>{p.odometer_km ? `${p.odometer_km} km` : ""}{p.odometer_km && p.fuel_level ? " · " : ""}{p.fuel_level ? `fuel ${p.fuel_level}` : ""}</div>}
                {p.note && <div className="truncate" title={p.note}>{p.note}</div>}
              </figcaption>
              {mine && fresh && (
                <button onClick={() => remove(p)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-[#B91C1C] opacity-0 group-hover:opacity-100 transition" aria-label="Remove photo" data-testid={`handover-remove-${p.id}`}><Trash2 size={12} /></button>
              )}
            </figure>
          );
        })}
      </div>

      {canAdd && (
        <div className="mt-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-1.5 bg-white border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 text-xs text-[#0a1f4d]"><Gauge size={12} className="text-[#64748B]" /><input inputMode="numeric" placeholder="Odometer km" value={meta.odometer_km} onChange={(e) => setMeta((m) => ({ ...m, odometer_km: e.target.value.replace(/\D/g, "") }))} className="w-full outline-none bg-transparent" data-testid={`handover-${phase}-odometer`} /></label>
            <label className="flex items-center gap-1.5 bg-white border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 text-xs text-[#0a1f4d]"><Fuel size={12} className="text-[#64748B]" /><input placeholder="Fuel (e.g. 3/4)" value={meta.fuel_level} onChange={(e) => setMeta((m) => ({ ...m, fuel_level: e.target.value }))} className="w-full outline-none bg-transparent" data-testid={`handover-${phase}-fuel`} /></label>
          </div>
          <input placeholder="Note (scratch on rear bumper…)" value={meta.note} onChange={(e) => setMeta((m) => ({ ...m, note: e.target.value }))} className="w-full bg-white border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 text-xs text-[#0a1f4d] outline-none focus:border-[#1d7ffd]" data-testid={`handover-${phase}-note`} />
          <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`handover-${phase}-file`} />
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary text-xs !py-2 w-full disabled:opacity-60" data-testid={`handover-${phase}-add`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {busy ? "Uploading…" : `Add ${phase} photo`}
          </button>
        </div>
      )}
    </div>
  );
}
